import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, Play, Image as ImageIcon, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { 
  ForYouSection,
  SponsoredArticlesSection, 
  AnnouncementsListSection, 
  TwoColumnScrollSection,
  VideoSection,
  BottomSponsoredSection 
} from './IgiheStyleSections';
import TrendingSection from './TrendingSection';
import MobileFeed from './MobileFeed';
import Pagination from './Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to generate URL-friendly slug from title
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

import { getTimeAgo } from '../lib/dateUtils';

// Custom Section Renderer - Renders admin-created sections on homepage
const CustomSectionRenderer = ({ section, isKinyarwanda, onArticleClick }) => {
  const articles = section.articles || [];
  
  if (!articles || articles.length === 0) return null;
  
  const getTitle = (article) => {
    if (isKinyarwanda && article.title_rw) return article.title_rw;
    return article.title_en || article.title || 'Untitled';
  };

  // Get badge color class
  const getBadgeColorClass = (color) => {
    const colors = {
      emerald: 'bg-[#047857]',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      pink: 'bg-pink-500',
      red: 'bg-red-500',
    };
    return colors[color] || 'bg-[#047857]';
  };

  return (
    <section className="my-8">
      {/* Section Header - Newspaper style */}
      <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-gray-900 dark:border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
            {section.title}
          </h3>
          {section.badge && (
            <span className={`px-2 py-0.5 text-[10px] font-bold text-white rounded ${getBadgeColorClass(section.badge_color)}`}>
              {section.badge}
            </span>
          )}
        </div>
      </div>
      
      {/* Articles Grid based on layout */}
      <div className={`grid gap-4 ${
        section.layout === '4small' ? 'grid-cols-2 md:grid-cols-4' :
        section.layout === '6small' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6' :
        section.layout === '2big-6small' ? 'grid-cols-2 md:grid-cols-4' :
        'grid-cols-1 md:grid-cols-3 lg:grid-cols-5' // 1big-4small default
      }`}>
        {articles.map((article, index) => {
          const title = getTitle(article);
          const articleUrl = `/news/${generateSlug(title)}`;
          const isBig = section.layout === '1big-4small' && index === 0;
          const isExternal = article.is_fetched || (article.link && !article.is_user_created);

          const handleClick = (e) => {
            if (onArticleClick) {
              e.preventDefault();
              onArticleClick(article);
              return;
            }
            if (isExternal && article.link) {
              e.preventDefault();
              window.open(article.link, '_blank', 'noopener,noreferrer');
            }
          };
          
          return (
            <Link 
              key={article.id || index} 
              to={articleUrl}
              onClick={handleClick}
              className={`block group cursor-pointer ${isBig ? 'md:col-span-2 md:row-span-2' : ''}`}
            >
              {/* Mobile: Card style */}
              <div className="md:hidden bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
                <div className="aspect-[16/10] bg-gray-200 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                  {article.image_url && (
                    <img
                      src={article.image_url}
                      alt={title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="p-2.5 flex-1">
                  <h4 className="text-xs font-medium text-gray-900 dark:text-white leading-snug group-hover:text-emerald-600 transition-colors line-clamp-2">
                    {title}
                  </h4>
                  <p className="text-[10px] text-gray-500 mt-1">{article.source || 'YegoNews'}</p>
                </div>
              </div>
              
              {/* Desktop: Newspaper style */}
              <div className="hidden md:flex flex-col h-full">
                <div className={`${isBig ? 'aspect-[16/9]' : 'aspect-[16/10]'} bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0`}>
                  {article.image_url && (
                    <img
                      src={article.image_url}
                      alt={title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="pt-2 flex-1">
                  <h4 className={`${isBig ? 'text-base' : 'text-sm'} font-medium text-gray-900 dark:text-white leading-snug group-hover:text-emerald-600 transition-colors line-clamp-2`}>
                    {title}
                  </h4>
                  <p className="text-[10px] text-gray-500 mt-1">{article.source || 'YegoNews'}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

// Large Featured Card - 9to5Google Style with Floating Green Card Accent Frame
const FeaturedCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title || 'Untitled');
  const articleUrl = `/news/${generateSlug(title)}`;
  
  // Check if it's an external/fetched article
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  // Handle click for external articles or in-house reader
  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCommentsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onArticleClick) {
      onArticleClick(article);
      setTimeout(() => {
        const commentsEl = document.getElementById('comments-section') || document.querySelector('.comments-container');
        if (commentsEl) commentsEl.scrollIntoView({ behavior: 'smooth' });
      }, 350);
    }
  };

  const categoryTag = (article.category || article.topic || article.source || 'YEGO NEWS').toUpperCase();
  const authorName = article.author || article.source || 'YEGO Staff';
  const authorAvatar = article.author_image || article.author_photo || article.user_avatar || null;
  const timeAgo = getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda);
  const [commentsCount, setCommentsCount] = useState(
    typeof article.comments_count === 'number'
      ? article.comments_count
      : (Array.isArray(article.comments) ? article.comments.length : 0)
  );

  useEffect(() => {
    if (typeof article.comments_count === 'number') {
      setCommentsCount(article.comments_count);
    }
    const artId = article.id || article._id;
    if (artId) {
      axios.get(`${BACKEND_URL}/api/comments/${artId}`)
        .then(res => {
          if (Array.isArray(res.data)) {
            setCommentsCount(res.data.length);
          }
        })
        .catch(() => {});
    }
  }, [article.id, article._id, article.comments_count]);

  useEffect(() => {
    const handleCommentChange = (e) => {
      const artId = String(article.id || article._id || '');
      if (e.detail?.articleId && String(e.detail.articleId) === artId) {
        if (typeof e.detail.count === 'number') {
          setCommentsCount(e.detail.count);
        }
      }
    };
    window.addEventListener('yego_comment_changed', handleCommentChange);
    return () => window.removeEventListener('yego_comment_changed', handleCommentChange);
  }, [article.id, article._id]);

  const getExcerpt = () => {
    const raw = (isKinyarwanda && article.content_rw) 
      ? article.content_rw 
      : (article.summary || article.description || article.content || '');
    if (!raw) return '';
    const clean = raw.replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim();
    return clean.length > 180 ? clean.substring(0, 180) + '...' : clean;
  };

  const excerpt = getExcerpt();

  return (
    <article className="group cursor-pointer pb-6">
      <Link to={articleUrl} onClick={handleClick} className="block">
        {/* 1. Category / Topic Tag */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            {categoryTag}
          </span>
        </div>

        {/* 2. Headline / Title */}
        <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white leading-[1.25] tracking-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors mb-2.5">
          {title}
        </h2>

        {/* 3. Byline Meta */}
        <div className="flex items-center gap-2 text-xs mb-1.5 flex-wrap">
          <div className="w-5 h-5 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center text-[10px] flex-shrink-0 shadow-xs">
            {authorAvatar ? (
              <img src={authorAvatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span>{(authorName || 'Y').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <span className="font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
            {authorName}
          </span>
          <span className="text-gray-300 dark:text-gray-700 font-light">|</span>
          <span className="text-gray-500 dark:text-gray-400 font-normal">
            {timeAgo}
          </span>
        </div>

        {/* 4. Comments Count */}
        <div 
          onClick={handleCommentsClick}
          className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-3 hover:underline w-fit"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span>{commentsCount} {isKinyarwanda ? 'Ibitekerezo' : 'Comments'}</span>
        </div>

        {/* 5. The Signature 9to5Google Floating Image Card with Green Accent Frame */}
        <div className="relative my-4 mx-2.5 sm:mx-3 group/img">
          {/* Top-Left Green Frame Accent */}
          <div 
            className="absolute -top-2.5 -left-2.5 w-1/4 h-[calc(100%+10px)] border-t-[2.5px] border-l-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
            aria-hidden="true" 
          />
          {/* Bottom-Right Green Frame Accent */}
          <div 
            className="absolute -bottom-2.5 -right-2.5 w-[calc(100%+10px)] h-1/4 border-b-[2.5px] border-r-[2.5px] border-emerald-600 dark:border-emerald-500 pointer-events-none z-0" 
            aria-hidden="true" 
          />

          {/* Foreground Image Card */}
          <div className="relative z-10 w-full aspect-[16/10] sm:aspect-[16/10] bg-white dark:bg-gray-900 overflow-hidden shadow-sm border border-gray-200/60 dark:border-gray-800">
            {article.image_url ? (
              <img
                src={article.image_url}
                alt={title}
                className="w-full h-full object-cover object-top sm:object-center group-hover/img:scale-[1.015] transition-transform duration-300"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                <ImageIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
              </div>
            )}
          </div>
        </div>

        {/* 6. Excerpt */}
        {excerpt && (
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-3 mb-1 font-normal line-clamp-3">
            {excerpt}
          </p>
        )}
      </Link>
    </article>
  );
};

// Medium Card - Newspaper style for both mobile and desktop
const MediumCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  
  // Check if it's an external/fetched article
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  // Handle click for external articles or in-house reader
  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link to={articleUrl} onClick={handleClick} className="block group h-full">
      <div className="flex flex-col h-full">
        <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-gray-400" />
            </div>
          )}
        </div>
        <div className="pt-2 flex-1 flex flex-col justify-between">
          <h3 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug group-hover:text-[#047857] dark:group-hover:text-emerald-400 transition-colors">
            {title}
          </h3>
          <div className="flex items-center gap-1.5 mt-1 sm:mt-1.5 text-[9px] sm:text-[10px] text-gray-400">
            <span>{article.source}</span>
            <span>•</span>
            <span>{getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Small Horizontal Card - Newspaper style
const SmallCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  
  // Check if it's an external/fetched article
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);

  // Handle click for external articles or in-house reader
  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link to={articleUrl} onClick={handleClick} className="flex gap-2.5 group py-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0 transition-colors">
      {/* Thumbnail */}
      {article.image_url && (
        <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800">
          <img
            src={article.image_url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      
      {/* Content - Full headline only */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <h4 className="text-xs font-medium text-gray-900 dark:text-white leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
          {title}
        </h4>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span>{article.source}</span>
          <span>•</span>
          <span>{getTimeAgo(article.published_at || article.created_at || article.date, isKinyarwanda)}</span>
        </div>
      </div>
    </Link>
  );
};

// In-Feed Ad Slot - Only shows if ads are enabled and there's an ad for this position
const InFeedAdSlot = ({ ad, slotPosition }) => {
  // Track impression when ad is displayed
  useEffect(() => {
    if (ad && ad.id) {
      axios.post(`${BACKEND_URL}/api/ads/${ad.id}/impression`).catch(() => {});
    }
  }, [ad]);

  // If no ad, don't render anything (clean look)
  if (!ad) return null;

  const handleClick = () => {
    if (ad && ad.id) {
      axios.post(`${BACKEND_URL}/api/ads/${ad.id}/click`).catch(() => {});
    }
  };

  return (
    <div className="my-6">
      <a 
        href={ad.link} 
        target="_blank" 
        rel="noopener noreferrer"
        onClick={handleClick}
        className="block"
      >
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-lg transition-shadow">
          {ad.image_url ? (
            <div className="relative">
              <img 
                src={ad.image_url} 
                alt={ad.title || 'Advertisement'} 
                className="w-full h-auto object-cover"
              />
              <div className="absolute top-2 left-2">
                <span className="px-2 py-0.5 bg-black/50 text-white text-[10px] rounded uppercase tracking-wider">
                  Ad
                </span>
              </div>
            </div>
          ) : (
            <div className="p-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white relative">
              <div className="absolute top-2 left-2">
                <span className="px-2 py-0.5 bg-black/30 text-white text-[10px] rounded uppercase tracking-wider">
                  Ad
                </span>
              </div>
              <div className="text-center pt-4">
                <h3 className="font-bold text-lg">{ad.title}</h3>
                {ad.description && <p className="text-sm opacity-90 mt-1">{ad.description}</p>}
              </div>
            </div>
          )}
          {ad.partner_name && (
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
              Sponsored by {ad.partner_name}
            </div>
          )}
        </div>
      </a>
    </div>
  );
};

// Category Section - Horizontal scroll of 2x2 grids on mobile, vertical grid on desktop
const CategorySection = ({ title, articles, badge, badgeColor = 'emerald', isKinyarwanda, linkTo }) => {
  if (!articles || articles.length === 0) return null;

  const colors = {
    emerald: 'bg-[#047857]/10 text-[#047857] dark:bg-[#047857]/20 dark:text-emerald-400',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    pink: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  };

  // Group articles into chunks of 4 for mobile 2x2 grid pages
  const chunkedArticles = [];
  for (let i = 0; i < articles.length; i += 4) {
    chunkedArticles.push(articles.slice(i, i + 4));
  }

  return (
    <section className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h2>
          {badge && (
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${colors[badgeColor]}`}>
              {badge}
            </span>
          )}
        </div>
        {linkTo && (
          <Link to={linkTo} className="flex items-center gap-1 text-xs text-[#D90429] dark:text-[#D90429] hover:underline font-medium">
            View all <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      
      {/* Mobile: Horizontal scroll of 2x2 grid pages */}
      <div className="sm:hidden overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        <div className="flex gap-3" style={{ width: 'max-content' }}>
          {chunkedArticles.slice(0, 3).map((chunk, chunkIndex) => (
            <div key={chunkIndex} className="w-[calc(100vw-32px)] flex-shrink-0 snap-start">
              <div className="grid grid-cols-2 gap-2">
                {chunk.map((article, index) => (
                  <MediumCard key={article.id || `${chunkIndex}-${index}`} article={article} isKinyarwanda={isKinyarwanda} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Desktop: Vertical scrollable grid */}
      <div className="hidden sm:block max-h-[320px] overflow-y-auto pr-1 scrollbar-thin">
        <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">
          {articles.slice(0, 12).map((article, index) => (
            <MediumCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} />
          ))}
        </div>
      </div>
    </section>
  );
};

const MainContentGrid = ({ 
  featuredArticles = [], 
  overflowArticles = [], // Articles that overflow from left sidebar
  categoryData = {},
  customSections = [],
  sponsoredArticles = [],
  announcements = [],
  videoArticles = [],
  onArticleClick,
  // Pagination props
  currentPage = 1,
  totalPages = 1,
  onPageChange
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';
  const [adsData, setAdsData] = useState({ ads_enabled: false, positions: {} });
  const [trendingArticles, setTrendingArticles] = useState([]);

  // Fetch ads data on mount
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/ads/all`);
        setAdsData(response.data);
      } catch (error) {
        console.log('Failed to fetch ads');
      }
    };
    fetchAds();
  }, []);

  // Fetch trending articles for mobile feed
  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/articles/trending?limit=6`);
        setTrendingArticles(response.data || []);
      } catch (error) {
        console.log('Failed to fetch trending');
      }
    };
    fetchTrending();
  }, []);

  // Helper to get ad for a position
  const getAdForPosition = (position) => {
    if (!adsData.ads_enabled || !adsData.positions) return null;
    const positionAds = adsData.positions[position] || [];
    return positionAds.length > 0 ? positionAds[0] : null;
  };

  // Combine all articles for mobile feed
  const allArticlesForMobile = [...featuredArticles, ...overflowArticles];

  // Get first 2 articles for featured section
  const mainFeatured = featuredArticles[0];
  const secondFeatured = featuredArticles[1];
  
  // Combine regular "rest" articles with overflow articles from sidebar
  // Overflow articles are older, so they go after the featured rest
  const restFeatured = featuredArticles.slice(2, 8);
  const moreStoriesArticles = [...restFeatured, ...overflowArticles];

  return (
    <div className="flex-1 min-w-0">
      {/* ===== MOBILE FEED - Only visible on mobile ===== */}
      <MobileFeed 
        articles={allArticlesForMobile}
        trendingArticles={trendingArticles}
        forYouArticles={sponsoredArticles.length > 0 ? sponsoredArticles : featuredArticles.slice(0, 12)}
        adsData={adsData}
        onArticleClick={onArticleClick}
        forYouSection={
          <ForYouSection 
            articles={sponsoredArticles.length > 0 ? sponsoredArticles : featuredArticles.slice(0, 12)}
            title={isKinyarwanda ? "Ibikureba" : "For You"}
            onArticleClick={onArticleClick}
          />
        }
        yegoTvSection={
          <VideoSection 
            articles={videoArticles.length > 0 ? videoArticles : featuredArticles.filter(a => a.video_url || a.has_video)}
            title="YEGOtv"
            onArticleClick={onArticleClick}
          />
        }
        sponsoredSection={
          <BottomSponsoredSection 
            articles={sponsoredArticles.length > 0 ? sponsoredArticles : featuredArticles.slice(3, 11)}
            title={isKinyarwanda ? "Kwamamaza" : "Sponsored"}
            onArticleClick={onArticleClick}
          />
        }
        announcementsSection={
          <AnnouncementsListSection 
            announcements={announcements}
            title={isKinyarwanda ? "Amatangazo amenyesha" : "Announcements"}
          />
        }
      />

      {/* ===== DESKTOP LAYOUT - Hidden on mobile ===== */}
      <div className="hidden md:block">
      {/* Hero Section - 2 Featured Articles */}
      {mainFeatured && (
        <section className="mb-8">
          {/* Desktop: Add divider line below hero */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 md:pb-6 md:border-b md:border-gray-200 dark:md:border-gray-800">
            {/* Main Featured */}
            <FeaturedCard article={mainFeatured} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
            
            {/* Second Featured or Grid */}
            {secondFeatured ? (
              <FeaturedCard article={secondFeatured} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
            ) : (
              <div className="bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <span className="text-gray-400">More content coming</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* In-Feed Ad 1 - After hero */}
      <InFeedAdSlot ad={getAdForPosition('in-feed-1')} slotPosition="in-feed-1" />

      {/* Trending Section - Horizontal scrolling trending articles */}
      <TrendingSection isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
      
      {/* Rest of Featured Articles - "Explore More" section (includes overflow from sidebar) */}
      {moreStoriesArticles.length > 0 && (
        <section className="my-8" data-testid="more-stories-section">
          {/* Mobile: Regular header */}
          <h2 className="md:hidden text-lg font-bold text-gray-900 dark:text-white mb-4">
            {isKinyarwanda ? 'Reba Ibindi' : 'Explore More'}
          </h2>
          {/* Desktop: Newspaper style header with line */}
          <div className="hidden md:block mb-4 pb-2 border-b-2 border-gray-900 dark:border-gray-100">
            <h2 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
              {isKinyarwanda ? 'Reba Ibindi' : 'Explore More'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {moreStoriesArticles.map((article, index) => (
              <MediumCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
            ))}
          </div>
        </section>
      )}

      {/* In-Feed Ad 2 */}
      <InFeedAdSlot ad={getAdForPosition('in-feed-2')} slotPosition="in-feed-2" />

      {/* ===== For You Section ===== */}
      <ForYouSection 
        articles={sponsoredArticles.length > 0 ? sponsoredArticles : featuredArticles.slice(0, 12)}
        title={isKinyarwanda ? "Ibikureba" : "For You"}
        className="my-8"
        onArticleClick={onArticleClick}
      />

      {/* ===== YEGOtv SECTION - Videos ===== */}
      <VideoSection 
        articles={videoArticles.length > 0 ? videoArticles : featuredArticles.filter(a => a.video_url || a.has_video)}
        title="YEGOtv"
        className="my-8"
        onArticleClick={onArticleClick}
      />

      {/* ===== Custom Sections from Admin (placed after YegoTV) ===== */}
      {customSections && customSections.length > 0 && customSections.map((section, index) => (
        <CustomSectionRenderer 
          key={section.id || index}
          section={section}
          isKinyarwanda={isKinyarwanda}
          onArticleClick={onArticleClick}
        />
      ))}

      {/* ===== Announcements Section (Amatangazo amenyesha) ===== */}
      <AnnouncementsListSection 
        announcements={announcements}
        title={isKinyarwanda ? "Amatangazo amenyesha" : "Announcements"}
        className="my-8"
      />

      {/* In-Feed Ad 3 */}
      <InFeedAdSlot ad={getAdForPosition('in-feed-3')} slotPosition="in-feed-3" />

      {/* In-Feed Ad 4 */}
      <InFeedAdSlot ad={getAdForPosition('in-feed-4')} slotPosition="in-feed-4" />

      {/* ===== Bottom Sponsored Section ===== */}
      <BottomSponsoredSection 
        articles={sponsoredArticles.length > 0 ? sponsoredArticles : featuredArticles.slice(3, 11)}
        title={isKinyarwanda ? "Kwamamaza" : "Sponsored"}
        className="my-8"
        onArticleClick={onArticleClick}
      />
      </div>
      {/* End Desktop Layout */}
    </div>
  );
};

export default MainContentGrid;
